import { IOrder, IOrderDetail, IOrderReview, IPositions } from '../types/Order';
import { getOrder } from './orderAdapater';
import { CONFIG } from '../../../lib/config';
import { getPaymentUrl } from '../../basket/libs/paymentLink';

const imageUrl = (image: string) => {
  if (!image) return '';
  if (image.startsWith('http')) return image;
  return `${CONFIG.file_url}${image}`;
};

export const getPositions = (item?: IPositions) => ({
  id: item?.id ?? 0,
  count: item?.count ?? 0,
  title: item?.title ?? '',
  price: item?.price ?? '',
  image: imageUrl(item?.image ?? ''),
});

export const postionsAdapter = (data?: IPositions[]) =>
  data?.map(item => getPositions(item)) ?? [];

export const orederReview = (item?: IOrderReview) => ({
  id: item?.id ?? 0,
  order_id: item?.order_id ?? 0,
  service_mark: item?.service_mark ?? 0,
  order_mark: item?.order_mark ?? 0,
  user_comment: item?.user_comment ?? '',
  operator_service: item?.operator_service ?? null,
  source: item?.source ?? '',
  client_id: item?.client_id ?? 0,
});

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

/** Статус платежа «не оплачен» — единственное, что бэкенд проверяет при отмене. */
const PAYMENT_STATUS_UNPAID = 1;
/** Статусы заказа, после которых отменять нечего: передан клиенту (5) и
    все отменённые (2, 7, 14). */
const FINAL_ORDER_STATUSES = new Set([2, 5, 7, 14]);

/**
 * Можно ли отменить заказ. Правило бэкенда — `last_payment_status_id == 1`:
 * оплата не подтверждена. Финальные статусы отсекаем сами: на них бэкенд всё
 * равно откажет, а кнопка на доставленном заказе выглядела бы ошибкой.
 */
export const canCancelOrder = (order?: IOrder) =>
  Boolean(order) &&
  order!.last_payment_status_id === PAYMENT_STATUS_UNPAID &&
  !FINAL_ORDER_STATUSES.has(order!.last_status_id);

export const orderDetailAdapter = (item?: IOrderDetail) => {
  const firstBonusPointsChange = item?.cart?.order?.bonusPointsChanges?.[0];
  const bonusPointsSpentAmount = toNumber(firstBonusPointsChange?.spentAmount);
  const bonusPointsEarnedAmount = toNumber(firstBonusPointsChange?.earnedAmount);
  const coupon = item?.cart?.order?.couponsInfo?.[0]?.coupon;
  const couponStatus = coupon?.status ?? '';
  const couponName = coupon?.pool?.name ?? '';
  const cartOrderTotalPrice = toNumber(item?.cart?.order?.totalPrice);
  const grandTotal = toNumber(item?.order?.grand_total);
  const discountAmount =
    cartOrderTotalPrice > 0 && cartOrderTotalPrice < grandTotal
      ? grandTotal - cartOrderTotalPrice
      : 0;
  // Итог — из расчёта корзины (там уже учтены скидки и баллы); grand_total —
  // запасной вариант, когда расчёта у заказа нет.
  const totalPrice = cartOrderTotalPrice > 0 ? cartOrderTotalPrice : grandTotal;
  const cartDeliveryCost = toNumber(item?.cart?.order?.deliveryCost);
  const deliveryPrice =
    cartDeliveryCost > 0
      ? cartDeliveryCost
      : Math.max(grandTotal - toNumber(item?.order?.total_amount), 0);

  return {
    address_title: item?.address_title ?? '',
    order: getOrder(item?.order),
    positions: postionsAdapter(item?.positions),
    card_id: item?.card_id ?? null,
    review: item?.review ? orederReview(item?.review) : null,
    fast_delivery: item?.fast_delivery ?? false,
    can_cancel: canCancelOrder(item?.order),
    // Ссылка есть только у неоплаченного онлайн-заказа; голый домен — не ссылка.
    payment_url: getPaymentUrl({ checkout_url: item?.checkout_url }),
    bonus_points_spent_amount: bonusPointsSpentAmount,
    bonus_points_earned_amount: bonusPointsEarnedAmount,
    is_promocode_can_be_used: couponStatus === 'CanBeUsed',
    promocode_name: couponName,
    discount_amount: discountAmount,
    total_price: totalPrice,
    delivery_price: deliveryPrice,
  };
};
